import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Spinner,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@wc/domain/authn";
import { formatDate } from "@wc/domain/dates";

import { INVITE_ERRORS } from "../constants";
import { redeemInvite } from "../core/redeem-invite";
import { validateInvite } from "../core/validate-invite";

/**
 * @param {object} props Component props
 * @param {string} props.token The invite token from the URL
 * @returns {React.ReactElement} Invite redemption UI
 */
export default function InviteRedemption({ token }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authData = useAuth();

  const [isValidating, setIsValidating] = React.useState(true);
  const [isRedeeming, setIsRedeeming] = React.useState(false);
  const [inviteData, setInviteData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [hasAttemptedRedeem, setHasAttemptedRedeem] = React.useState(false);

  React.useEffect(() => {
    const validate = async () => {
      try {
        const result = await validateInvite(token);
        if (!result.valid) {
          setError(result.error || INVITE_ERRORS.NOT_FOUND);
        } else {
          setInviteData(result);
        }
      } catch (err) {
        console.error("Invite validation error:", err);
        setError(err.code || INVITE_ERRORS.NOT_FOUND);
      } finally {
        setIsValidating(false);
      }
    };

    validate();
  }, [token]);

  React.useEffect(() => {
    const redeem = async () => {
      if (
        !inviteData ||
        authData.isLoading() ||
        !authData.isLoggedIn() ||
        hasAttemptedRedeem
      ) {
        return;
      }

      setHasAttemptedRedeem(true);
      setIsRedeeming(true);
      try {
        const result = await redeemInvite(token);

        if (!result.success) {
          console.error("Invite redemption failed:", result.error);
          setError(result.error || "REDEEM_FAILED");
          setIsRedeeming(false);
          return;
        }

        navigate("/");
      } catch (err) {
        console.error("Invite redemption error:", err);
        setError(err.code || "REDEEM_FAILED");
        setIsRedeeming(false);
      }
    };

    redeem();
  }, [inviteData, authData, token, navigate, hasAttemptedRedeem]);

  const handleSignIn = () => {
    const returnUrl = encodeURIComponent(`/invite/${token}`);
    navigate(`/login?returnUrl=${returnUrl}`);
  };

  if (isValidating || authData.isLoading()) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    const errorMessages = {
      [INVITE_ERRORS.NOT_FOUND]: t("invites.errors.notFound"),
      [INVITE_ERRORS.EXPIRED]: t("invites.errors.expired"),
      [INVITE_ERRORS.MAX_USES_REACHED]: t("invites.errors.maxUsesReached"),
      [INVITE_ERRORS.INACTIVE]: t("invites.errors.inactive"),
      [INVITE_ERRORS.ALREADY_TEAM_MEMBER]: t("invites.errors.alreadyMember"),
    };

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="flex flex-col items-center gap-2">
            <h1 className="text-danger text-xl font-semibold">
              {t("invites.invalidInvite")}
            </h1>
          </CardHeader>
          <CardBody className="text-center">
            <p className="text-default-500">
              {errorMessages[error] || t("invites.errors.generic")}
            </p>
          </CardBody>
          <CardFooter className="justify-center">
            <Button color="primary" onPress={() => navigate("/")}>
              {t("invites.goHome")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (authData.isLoggedIn() && isRedeeming) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <Spinner size="lg" />
        <p className="text-default-500">{t("invites.joiningTeam")}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center gap-2">
          <h1 className="text-xl font-semibold">
            {t("invites.youAreInvited")}
          </h1>
        </CardHeader>
        <CardBody className="text-center">
          <p className="text-default-600 mb-2">
            {t("invites.invitedToJoin", {
              teamName: inviteData.teamName,
              orgName: inviteData.orgName,
            })}
          </p>
          {inviteData.expiresAt && (
            <p className="text-default-400 text-sm">
              {t("invites.expiresAt", {
                date: formatDate(inviteData.expiresAt),
              })}
            </p>
          )}
        </CardBody>
        <CardFooter className="justify-center">
          <Button color="primary" size="lg" onPress={handleSignIn}>
            {t("invites.signInToJoin")}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
